import {AttrsSchema, Component} from 'panel';
import 'regenerator-runtime/runtime'; // required by gcs-browser-upload

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {
  fetchLookupTableProperties,
  lookupTablesStore,
  registerTempLookupTableMapping,
} from 'common/report/stores/lookup-tables';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {reporter} from 'common/report/error-reporting';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';

import {template} from './template';
import {
  trackEphemeralMappingComplete,
  trackImportComplete,
  trackInfoClick,
  trackSelectTable,
  trackUploadComplete,
  trackUploadMarkedReady,
} from './tracking';
import css from './index.inline.styl';
import {confirmPropertyMapOverwrite, propIneligibleForMappingReason} from './util';
import {markLookupTableReady, uploadLookupTable} from './lookup-tables-api';

export type MapLookupTableModalType = `import` | `temporary-map`;

interface LookupTableSectionItem {
  label: string;
  dataGroupId: string;
  isNewTable?: true;
}

/** A PropertyItem with the `value` (aka propertyName) key required.
 *
 * `value`, `resourceType`, `type`, and `propertyDefaultType` are required */
export type PropertyItemWithValue = PropertySectionItem & {value: string | string[]};

type MapLookupTableModalState = {
  selectedLookupTable?: LookupTableSectionItem;
  selectedProperty?: PropertyItemWithValue | EmptyObj;
  lookupTables: LookupTableSectionItem[];
  hideSelect: boolean;
  selectTableErrorMessage?: string;
  fileProcessingDescription?: string;
  uploadedFilename?: string;
  invalidPropertyErrorMessage?: string;
  savePending: boolean;
  requireOverwriteConfirmation: boolean;
  overwriteModalOpen: boolean;
};

export type MapLookupTableModalAttrs = {
  type: MapLookupTableModalType;
  open: boolean;
  'project-id': number;
  'workspace-id': number;
  'user-id': number;
  'project-has-people': boolean;
  'selected-property'?: PropertyItemWithValue;
  'event-names'?: string[];
  'disable-property-selection'?: boolean;
  elref?: string;
};

type MapLookupTableModalJsxProps = {
  attrs: MapLookupTableModalAttrs;
};

export class MapLookupTableModal extends Component<MapLookupTableModalState, MapLookupTableModalAttrs> {
  static get attrsSchema(): AttrsSchema<MapLookupTableModalAttrs> {
    return {
      type: {type: `string`, description: `MapLookupTableModalType`},
      open: `boolean`,
      'project-id': `number`,
      'workspace-id': `number`,
      'user-id': `number`,
      'project-has-people': `boolean`,
      'selected-property': {type: `json`, default: null},
      'event-names': {type: `json`, default: null},
      'disable-property-selection': `boolean`,
      elref: `string`,
    };
  }
  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        selectedLookupTable: null,
        selectedProperty: null,
        lookupTables: [],
        hideSelect: false,
        selectTableErrorMessage: null,
        fileProcessingDescription: null,
        uploadedFilename: null,
        invalidPropertyErrorMessage: null,
        savePending: false,
        requireOverwriteConfirmation: false,
        overwriteModalOpen: false,
      },
      helpers: {
        getModalTitle: () =>
          this.attr(`type`) === `temporary-map` ? `Create Lookup Table Mapping` : `Import Lookup Table`,
        getSelectedLabel: () => this.state.selectedLookupTable?.label || `Select Existing`,
        getLookupTableSections: () => {
          return [{items: this.state.lookupTables}];
        },
        getFileUploadBlockAttrs: () => {
          const {fileProcessingDescription, uploadedFilename} = this.state;
          return {
            label: `Import CSV`,
            'enable-delete': true,
            disabled: this.state.savePending,
            ...(fileProcessingDescription ? {'processing-description': fileProcessingDescription} : {}),
            ...(uploadedFilename ? {'completed-filename': uploadedFilename} : {}),
          };
        },
        getQueryBlockAttrs: () => {
          return {
            type: `properties`,
            theme: `select`,
            'project-id': this.attr(`project-id`),
            'workspace-id': this.attr(`workspace-id`),
            'user-id': this.attr(`user-id`),
            'project-has-people': this.attr(`project-has-people`),
            'event-names': this.attr(`event-names`),
            'exclude-lookup-table-properties': true,
            selected: this.state.selectedProperty,
            disabled: this.attr(`disable-property-selection`) || this.state.savePending,
            'include-custom-properties': `persisted-only`,
            removable: this.attr(`type`) === `import` && Boolean(this.state.selectedProperty),
            tabindex: 0,
          };
        },
        selectLookupTable: (ev) => {
          const table = ev.detail.item;
          this.update({selectedLookupTable: table, selectTableErrorMessage: null});
          trackSelectTable(this, {
            dataGroupId: table.dataGroupId,
            isNewTable: false,
            source: this.getSourceTrackingProp(),
          });
        },
        handleModalClose: (ev) => {
          if (ev.detail.state === `closed`) {
            this.dispatchCloseEvent();
          }
        },
        handleConfirmationModal: (ev) => {
          if (ev.type === `submit` && ev.detail.action === `confirm`) {
            this.update({requireOverwriteConfirmation: false, overwriteModalOpen: false});
            this.save();
          } else {
            this.update({overwriteModalOpen: false});
          }
        },
        handleCancel: () => {
          if (this.state.selectedLookupTable?.isNewTable) {
            // best effort to delete some uploaded lookup tables that won't ever be marked ready
            this.deleteNewLookupTable();
          }
          this.dispatchCloseEvent();
        },

        uploadLookupTableCSV: async (ev) => {
          this.update({
            selectedLookupTable: null,
            selectTableErrorMessage: null,
            fileProcessingDescription: `Uploading file...`,
          });

          const file = ev.detail.file;
          const deferMarkReady = true;
          const onUploadProgress = (percent) => {
            this.update({fileProcessingDescription: `Uploading file (${percent})`});
          };
          const onProcessingStarted = () => {
            this.update({fileProcessingDescription: `Processing file...`});
          };
          const result = await uploadLookupTable({
            file,
            projectId: this.attr(`project-id`),
            workspaceId: this.attr(`workspace-id`),
            deferMarkReady,
            onUploadProgress,
            onProcessingStarted,
          });

          if (result.succeeded === true) {
            const {name: fileName, size: fileSize} = file;
            const {
              dataGroupId,
              rowsUploaded,
              rowsImported,
              numColumns,
              secondsToGetUrl,
              secondsToUpload,
              secondsToProcess,
              secondsTotal,
              droppedRowsWarningMessage,
              warningReason,
            } = result;
            this.update({
              selectTableErrorMessage: droppedRowsWarningMessage || null,
              uploadedFilename: fileName,
              hideSelect: true,
              fileProcessingDescription: null,
              selectedLookupTable: {dataGroupId, label: fileName, isNewTable: true},
            });
            trackUploadComplete({
              el: this,
              fileSize,
              isNewLookupTable: true,
              numberOfRowsProcessed: rowsImported,
              numberOfRowsUploaded: rowsUploaded,
              uploadStatus: droppedRowsWarningMessage ? `Warning` : `Success`,
              uploadStatusReason: warningReason ? warningReason : null,
              timeToGetUrl: secondsToGetUrl,
              timeToUpload: secondsToUpload,
              timeToProcess: secondsToProcess,
              totalTime: secondsTotal,
              numColumns,
              dataGroupId,
              source: this.getSourceTrackingProp(),
              deferMarkReady,
            });
            trackSelectTable(this, {dataGroupId, isNewTable: true, source: this.getSourceTrackingProp()});
          } else {
            this.setUploadAbortedState(result.errorMessage);
          }
        },

        handleDeleteUpload: () => {
          this.deleteNewLookupTable();
        },

        handleSelectProperty: (ev) => {
          let invalidPropertyErrorMessage;
          let requireOverwriteConfirmation = false;
          if (this.attr(`type`) === `temporary-map`) {
            invalidPropertyErrorMessage = propIneligibleForMappingReason(ev.detail.rawItem, true);
          } else {
            invalidPropertyErrorMessage = propIneligibleForMappingReason(ev.detail.rawItem);
            const autoTriggered = ev.detail.autoTriggered ? ev.detail.autoTriggered : false;
            requireOverwriteConfirmation =
              !invalidPropertyErrorMessage && confirmPropertyMapOverwrite(ev.detail.rawItem, autoTriggered);
          }
          this.update({selectedProperty: ev.detail.item, invalidPropertyErrorMessage, requireOverwriteConfirmation});
        },

        handleRemoveProperty: () => {
          this.update({selectedProperty: null, invalidPropertyErrorMessage: null, requireOverwriteConfirmation: false});
        },

        shouldDisableConfirm: (): boolean => {
          return (
            !this.state.selectedLookupTable ||
            (this.attr(`type`) === `temporary-map` && !this.state.selectedProperty?.resourceType) ||
            Boolean(this.state.invalidPropertyErrorMessage) ||
            this.state.savePending
          );
        },

        getConfirmButtonLabel: () => (this.attr(`type`) === `temporary-map` ? `Apply` : `Save`),

        handleConfirmClick: () => {
          if (this.attr(`type`) === `temporary-map`) {
            this.createTemporaryMapping();
          } else if (this.state.requireOverwriteConfirmation) {
            this.update({overwriteModalOpen: true});
          } else if (this.attr(`type`) === `import`) {
            this.save();
          }
        },

        trackInfoClick: () => trackInfoClick(this, {source: this.getSourceTrackingProp()}),
      },
    };
  }

  public saveAborted() {
    this.update({savePending: false});
  }

  private async save() {
    // ideally this component would handle saving the property mapping, but we first need to extract the api client for property mapping
    // out of lexicon
    this.update({savePending: true});
    const {dataGroupId} = this.state.selectedLookupTable;
    const {uploadedFilename} = this.state;

    try {
      await markLookupTableReady(this.attr(`project-id`), this.attr(`workspace-id`), dataGroupId, uploadedFilename);
    } catch (err) {
      addToast({message: `Error saving lookup table. Please try again.`, type: ToastType.ERROR});
      reporter.error(err, `[lookup] Error marking table ready`);
      this.update({savePending: false});
      return;
    }
    trackUploadMarkedReady(this, {source: this.getSourceTrackingProp(), dataGroupId});
    trackImportComplete(this, {
      dataGroupId,
      source: this.getSourceTrackingProp(),
      property: this.state.selectedProperty,
    });
    await lookupTablesStore.refresh();
    fetchLookupTableProperties(dataGroupId, this.attr(`project-id`), this.attr(`workspace-id`));
    this.dispatchEvent(
      new CustomEvent(`save`, {
        detail: {
          selectedProperty: this.state.selectedProperty,
          selectedDataGroupId: dataGroupId,
        },
      }),
    );
  }

  private async createTemporaryMapping() {
    this.update({savePending: true});

    const {dataGroupId} = this.state.selectedLookupTable;
    const isNewTable = Boolean(this.state.selectedLookupTable.isNewTable);
    const {uploadedFilename} = this.state;
    if (isNewTable) {
      try {
        await markLookupTableReady(this.attr(`project-id`), this.attr(`workspace-id`), dataGroupId, uploadedFilename);
        trackUploadMarkedReady(this, {source: this.getSourceTrackingProp(), dataGroupId});
      } catch (err) {
        addToast({message: `Error saving lookup table. Please try again.`, type: ToastType.ERROR});
        reporter.error(err, `[lookup] Error marking table ready`);
        this.update({savePending: false});
        return;
      }
      await lookupTablesStore.refresh();
    }

    const property = this.state.selectedProperty as PropertyItemWithValue;

    const registerResult = registerTempLookupTableMapping(property, dataGroupId);
    if (registerResult.succeeded === true) {
      const message = isNewTable
        ? `Applied temporary mapping to new lookup table`
        : `Applied temporary lookup table mapping`;
      addToast({
        message,
        type: ToastType.SUCCESS,
      });
      trackEphemeralMappingComplete(this, {property, dataGroupId, isNewTable, source: this.getSourceTrackingProp()});
      this.dispatchCloseEvent();
    } else {
      addToast({message: registerResult.errorMessage, type: ToastType.ERROR});
      this.update({savePending: false});
    }
  }

  private deleteNewLookupTable() {
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);
    const dataGroupId = this.state.selectedLookupTable.dataGroupId;
    lookupTablesStore.delete(dataGroupId, {projectId, workspaceId, dataGroupIds: [dataGroupId]});
    this.update({
      uploadedFilename: null,
      selectedLookupTable: null,
      selectTableErrorMessage: null,
    });
    // Delaying this update prevents a flicker of horizontal layout shift that happens when
    // the select gets shown while `mp-file-uploader` is still displaying uploadedFilename
    requestAnimationFrame(() => {
      this.update({hideSelect: false});
    });
  }

  private dispatchCloseEvent() {
    this.dispatchEvent(new CustomEvent(`close`));
  }

  private resetState() {
    const {lookupTables, ...defaultStateWithoutTables} = this.config.defaultState;
    this.update({...defaultStateWithoutTables});
  }

  private setUploadAbortedState(selectTableErrorMessage) {
    this.update({
      selectTableErrorMessage,
      fileProcessingDescription: null,
    });
  }

  private getSourceTrackingProp() {
    return this.attr(`type`) === `import` ? `lexicon` : `query-builder`;
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected-property`) {
      // assumes property is validated via propIneligibleForMappingReason before the attribute is set
      this.update({selectedProperty: this.attr(`selected-property`)});
    } else if (attr === `open` && !this.attr(`open`)) {
      requestAnimationFrame(() => this.resetState());
    }
  }

  private lookupTablesStoreCallback;
  connectedCallback() {
    super.connectedCallback();

    this.lookupTablesStoreCallback = () => {
      const lookupTables = Object.values(lookupTablesStore.value)
        .filter((table) => {
          // Filtering out lookup tables here since this is used for accessing the list of csvs
          return table.can_view || isAllowedSuperadminOverride(table);
        })
        .map((table) => {
          return {dataGroupId: table.id, label: table.name};
        });
      this.update({lookupTables});
    };

    // TODO(j) may need to fetch when in Lexicon
    lookupTablesStore.subscribe(this.lookupTablesStoreCallback);
    if (lookupTablesStore.hasFetchedValue()) {
      this.lookupTablesStoreCallback();
    }
  }

  disconnectedCallback() {
    lookupTablesStore.unsubscribe(this.lookupTablesStoreCallback);
    this.lookupTablesStoreCallback = null;

    super.disconnectedCallback();
  }
}

export const MapLookupTableModalEl = defineAndMakeJsxFactory<MapLookupTableModalJsxProps>(
  `mp-map-lookup-table-modal`,
  MapLookupTableModal,
);
export const MapLookupTableModalReact = panel2react<MapLookupTableModalJsxProps>(
  `mp-map-lookup-table-modal`,
  MapLookupTableModal,
);
